/**
 * Los gráficos.
 *
 * ---------------------------------------------------------------------------
 *  Por qué están escritos a mano y no con una librería
 * ---------------------------------------------------------------------------
 *
 * Una librería de gráficos pesa entre 100 y 400 KB, trae su propio sistema de temas
 * que hay que aprenderse, y hay que pelearse con ella para que respete el modo claro
 * y el oscuro. Estos cuatro gráficos son unas 400 líneas de SVG y no pesan nada.
 *
 * ---------------------------------------------------------------------------
 *  Lo que hace que un gráfico sirva o no sirva
 * ---------------------------------------------------------------------------
 *
 * 1. **Se puede leer sin colores.** Todas las series llevan su nombre escrito y su
 *    cifra. Un gráfico que solo se entiende distinguiendo el rojo del verde deja
 *    fuera a quien no los distingue.
 * 2. **No miente con la escala.** Los ejes empiezan en cero cuando son importes: un
 *    gráfico de barras que empieza en 500.000 hace parecer enorme una diferencia
 *    pequeña.
 * 3. **Dice cuando no hay datos**, en lugar de dibujar un círculo vacío que parece un
 *    error.
 * 4. **Respeta el tema.** Los colores salen de las variables de CSS, así que cambian
 *    solos con el modo claro y el oscuro.
 */

import { useId, useState } from 'react';

import { formatCOP } from '@eldescuadre/core';

/** Un trozo de un gráfico: lo que se le enseña al pasar por encima. */
export interface Slice {
  label: string;
  value: number;
  /** Color. Si no se da, se reparte de la paleta. */
  color?: string | undefined;
}

/** Un punto de una serie temporal. */
export interface Point {
  label: string;
  value: number;
  /** Un segundo valor, para las comparaciones (por ejemplo, los gastos). */
  second?: number | undefined;
}

/**
 * La paleta de los gráficos.
 *
 * Son los colores de la marca, ordenados para que dos trozos vecinos nunca se
 * parezcan. Es lo que hace legible una dona de ocho categorías.
 */
export const CHART_COLORS = [
  'rgb(var(--ed-gold-rgb))',
  'rgb(var(--ed-cyan-rgb))',
  'rgb(var(--ed-coral-rgb))',
  'rgb(var(--ed-success-rgb))',
  '#8b5cf6',
  '#f59e0b',
  '#0891b2',
  '#65a30d',
  '#db2777',
  '#7c3aed',
  '#0d9488',
  '#b45309',
] as const;

function colorAt(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length]!;
}

/** Un importe corto para los ejes: «1,2 M» en vez de «1.200.000». */
function shortMoney(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)} M`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)} k`;
  return String(Math.round(value));
}

/** El mensaje de cuando no hay nada que dibujar. */
function Empty({ message }: { message: string }): React.JSX.Element {
  return (
    <p className="flex min-h-[180px] items-center justify-center px-4 text-center text-sm text-ed-text-muted">
      {message}
    </p>
  );
}

/** El texto que leen los lectores de pantalla, porque un SVG no dice nada. */
function useDescription(parts: string[]): string {
  return parts.join('. ');
}

// ---------------------------------------------------------------------------
//  Dona — el reparto por categorías
// ---------------------------------------------------------------------------

export function DonutChart({
  data,
  centerLabel = 'Total',
  emptyMessage = 'Sin datos en el período.',
}: {
  data: Slice[];
  centerLabel?: string;
  emptyMessage?: string;
}): React.JSX.Element {
  const [active, setActive] = useState<number | null>(null);

  const clean = data.filter((slice) => slice.value > 0);
  const total = clean.reduce((acc, slice) => acc + slice.value, 0);

  const description = useDescription([
    'Gráfico de dona del reparto por categorías',
    ...clean.map((slice) => `${slice.label}: ${formatCOP(slice.value)}`),
  ]);

  if (clean.length === 0 || total === 0) return <Empty message={emptyMessage} />;

  // Radio y circunferencia: se dibuja con `stroke-dasharray` sobre un círculo, que
  // es la forma más corta y la que mejor se adapta a cualquier tamaño.
  const radius = 60;
  const circumference = 2 * Math.PI * radius;

  let accumulated = 0;
  const slices = clean.map((slice, index) => {
    const fraction = slice.value / total;
    const offset = accumulated;
    accumulated += fraction;
    return { ...slice, fraction, offset, color: slice.color ?? colorAt(index) };
  });

  const shown = active !== null ? slices[active] : null;

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-6">
      <div className="relative shrink-0">
        <svg
          viewBox="0 0 160 160"
          className="h-[168px] w-[168px] -rotate-90"
          role="img"
          aria-label={description}
        >
          {slices.map((slice, index) => (
            <circle
              key={slice.label}
              cx="80"
              cy="80"
              r={radius}
              fill="none"
              stroke={slice.color}
              strokeWidth={active === index ? 26 : 20}
              strokeDasharray={`${slice.fraction * circumference} ${circumference}`}
              strokeDashoffset={-slice.offset * circumference}
              className="cursor-pointer transition-all"
              onMouseEnter={() => setActive(index)}
              onMouseLeave={() => setActive(null)}
            />
          ))}
        </svg>

        {/* El centro: el total, o el trozo que se está mirando. */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[10px] uppercase tracking-wider text-ed-text-muted">
            {shown ? shown.label : centerLabel}
          </span>
          <span className="ed-money text-sm font-bold">
            {formatCOP(shown ? shown.value : total)}
          </span>
          {shown && (
            <span className="text-[10px] text-ed-text-muted">
              {Math.round(shown.fraction * 100)} % del total
            </span>
          )}
        </div>
      </div>

      {/* La lista al lado. Es lo que hace que el gráfico se entienda **sin** mirar
          los colores, y lo que permite leer las cifras exactas. */}
      <ul className="w-full min-w-0 space-y-1.5">
        {slices.map((slice, index) => (
          <li key={slice.label}>
            <button
              type="button"
              className={`flex w-full items-center gap-2 rounded-ed px-2 py-1.5 text-left text-sm transition-colors ${
                active === index ? 'bg-ed-bg-elevated/70' : 'hover:bg-ed-bg-elevated/40'
              }`}
              onMouseEnter={() => setActive(index)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(index)}
              onBlur={() => setActive(null)}
            >
              <span
                className="h-3 w-3 shrink-0 rounded-full"
                style={{ background: slice.color }}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 truncate">{slice.label}</span>
              <span className="ed-money shrink-0 text-xs font-semibold">
                {formatCOP(slice.value)}
              </span>
              <span className="w-9 shrink-0 text-right text-xs text-ed-text-muted">
                {Math.round(slice.fraction * 100)} %
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Barras — comparar dos cosas mes a mes
// ---------------------------------------------------------------------------

export function BarChart({
  data,
  firstLabel = 'Ingresos',
  secondLabel = 'Gastos',
  emptyMessage = 'Sin datos en el período.',
}: {
  data: Point[];
  firstLabel?: string;
  secondLabel?: string;
  emptyMessage?: string;
}): React.JSX.Element {
  const [active, setActive] = useState<number | null>(null);

  const clean = data.filter((point) => point.value !== 0 || (point.second ?? 0) !== 0);

  const description = useDescription([
    `Gráfico de barras comparando ${firstLabel} y ${secondLabel}`,
    ...clean.map(
      (point) => `${point.label}: ${firstLabel} ${formatCOP(point.value)}, ${secondLabel} ${formatCOP(point.second ?? 0)}`,
    ),
  ]);

  if (clean.length === 0) return <Empty message={emptyMessage} />;

  // La escala empieza en cero **siempre**. Un gráfico de importes que no empieza en
  // cero exagera las diferencias y hace tomar malas decisiones.
  const max = Math.max(...clean.map((p) => Math.max(p.value, p.second ?? 0)), 1);
  const height = 140;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-3 text-xs text-ed-text-muted">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-ed-gold" aria-hidden="true" />
          {firstLabel}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-ed-coral" aria-hidden="true" />
          {secondLabel}
        </span>
      </div>

      <div className="flex items-end gap-1 overflow-x-auto pb-1" style={{ height }}>
        {clean.map((point, index) => (
          <div
            key={point.label}
            className="flex h-full min-w-[2.5rem] flex-1 flex-col justify-end gap-1"
            onMouseEnter={() => setActive(index)}
            onMouseLeave={() => setActive(null)}
          >
            <div className="flex h-full items-end justify-center gap-0.5">
              <div
                className="w-1/2 max-w-[22px] rounded-t bg-ed-gold transition-all"
                style={{ height: `${Math.max((point.value / max) * 100, point.value > 0 ? 2 : 0)}%` }}
                title={`${firstLabel}: ${formatCOP(point.value)}`}
              />
              <div
                className="w-1/2 max-w-[22px] rounded-t bg-ed-coral transition-all"
                style={{
                  height: `${Math.max(((point.second ?? 0) / max) * 100, (point.second ?? 0) > 0 ? 2 : 0)}%`,
                }}
                title={`${secondLabel}: ${formatCOP(point.second ?? 0)}`}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Los nombres van debajo, y el que se está mirando se resalta. */}
      <div className="flex gap-1 border-t border-ed-bg-border/30 pt-1">
        {clean.map((point, index) => (
          <span
            key={point.label}
            className={`min-w-[2.5rem] flex-1 truncate text-center text-[10px] ${
              active === index ? 'font-semibold text-ed-gold' : 'text-ed-text-muted'
            }`}
          >
            {point.label}
          </span>
        ))}
      </div>

      {active !== null && clean[active] && (
        <p className="mt-2 rounded-ed bg-ed-bg-elevated/50 px-2 py-1.5 text-xs">
          <span className="font-medium">{clean[active]!.label}</span> ·{' '}
          <span className="text-ed-gold">
            {firstLabel} {formatCOP(clean[active]!.value)}
          </span>{' '}
          ·{' '}
          <span className="text-ed-coral">
            {secondLabel} {formatCOP(clean[active]!.second ?? 0)}
          </span>
        </p>
      )}

      <p className="sr-only">{description}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Línea — la tendencia
// ---------------------------------------------------------------------------

export function LineChart({
  data,
  label = 'Evolución',
  emptyMessage = 'Sin datos en el período.',
}: {
  data: Point[];
  label?: string;
  emptyMessage?: string;
}): React.JSX.Element {
  const [active, setActive] = useState<number | null>(null);
  const gradientId = useId();

  const clean = data.filter((point) => Number.isFinite(point.value));

  const description = useDescription([
    `Gráfico de línea de ${label}`,
    ...clean.map((point) => `${point.label}: ${formatCOP(point.value)}`),
  ]);

  if (clean.length < 2) return <Empty message={emptyMessage} />;

  const width = 600;
  const height = 160;
  const padding = { top: 12, right: 8, bottom: 8, left: 8 };

  const values = clean.map((p) => p.value);
  const max = Math.max(...values);
  const min = Math.min(...values, 0);
  const span = max - min || 1;

  const x = (index: number): number =>
    padding.left + (index / (clean.length - 1)) * (width - padding.left - padding.right);
  const y = (value: number): number =>
    padding.top + (1 - (value - min) / span) * (height - padding.top - padding.bottom);

  const line = clean.map((point, index) => `${x(index)},${y(point.value)}`).join(' ');
  const area = `${padding.left},${height - padding.bottom} ${line} ${width - padding.right},${height - padding.bottom}`;

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        style={{ height: 160 }}
        role="img"
        aria-label={description}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(var(--ed-cyan-rgb))" stopOpacity="0.35" />
            <stop offset="100%" stopColor="rgb(var(--ed-cyan-rgb))" stopOpacity="0" />
          </linearGradient>
        </defs>

        <polygon points={area} fill={`url(#${gradientId})`} />
        <polyline
          points={line}
          fill="none"
          stroke="rgb(var(--ed-cyan-rgb))"
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        {clean.map((point, index) => (
          <circle
            key={point.label}
            cx={x(index)}
            cy={y(point.value)}
            r={active === index ? 6 : 3.5}
            fill="rgb(var(--ed-bg-card-rgb))"
            stroke="rgb(var(--ed-cyan-rgb))"
            strokeWidth="2.5"
            vectorEffect="non-scaling-stroke"
            className="cursor-pointer"
            onMouseEnter={() => setActive(index)}
            onMouseLeave={() => setActive(null)}
          />
        ))}
      </svg>

      <div className="flex justify-between px-1 pt-1">
        <span className="text-[10px] text-ed-text-muted">{clean[0]!.label}</span>
        {active !== null && clean[active] ? (
          <span className="text-xs font-medium">
            {clean[active]!.label}:{' '}
            <span className="ed-money text-ed-cyan">{formatCOP(clean[active]!.value)}</span>
          </span>
        ) : (
          <span className="text-[10px] text-ed-text-muted">
            {shortMoney(min)} – {shortMoney(max)}
          </span>
        )}
        <span className="text-[10px] text-ed-text-muted">{clean[clean.length - 1]!.label}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Dispersión — buscar la relación entre dos cosas
// ---------------------------------------------------------------------------

export function ScatterChart({
  data,
  xLabel = 'Día del mes',
  yLabel = 'Gasto del día',
  emptyMessage = 'Sin datos suficientes para comparar.',
}: {
  data: Array<{ x: number; y: number; label: string }>;
  xLabel?: string;
  yLabel?: string;
  emptyMessage?: string;
}): React.JSX.Element {
  if (data.length < 3) return <Empty message={emptyMessage} />;

  const width = 560;
  const height = 180;
  const maxX = Math.max(...data.map((p) => p.x), 1);
  const maxY = Math.max(...data.map((p) => p.y), 1);

  const x = (value: number): number => 34 + (value / maxX) * (width - 44);
  const y = (value: number): number => height - 22 - (value / maxY) * (height - 34);

  const description = useDescription([
    `Gráfico de dispersión de ${yLabel} frente a ${xLabel}`,
    ...data.map((point) => `${point.label}: ${formatCOP(point.y)}`),
  ]);

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: 180 }} role="img" aria-label={description}>
        {/* Los ejes, como líneas de referencia discretas. */}
        <line x1="34" y1={height - 22} x2={width - 10} y2={height - 22} stroke="rgb(var(--ed-bg-border-rgb))" strokeWidth="1" />
        <line x1="34" y1="12" x2="34" y2={height - 22} stroke="rgb(var(--ed-bg-border-rgb))" strokeWidth="1" />

        <text x="34" y="9" fontSize="9" fill="rgb(var(--ed-text-muted-rgb))">
          {shortMoney(maxY)}
        </text>

        {data.map((point) => (
          <circle
            key={point.label}
            cx={x(point.x)}
            cy={y(point.y)}
            r="5"
            fill="rgb(var(--ed-gold-rgb))"
            fillOpacity="0.65"
            stroke="rgb(var(--ed-gold-rgb))"
            strokeWidth="1.5"
          >
            <title>{`${point.label}: ${formatCOP(point.y)}`}</title>
          </circle>
        ))}

        <text x={width - 10} y={height - 6} fontSize="9" fill="rgb(var(--ed-text-muted-rgb))" textAnchor="end">
          {xLabel}
        </text>
      </svg>

      <p className="mt-1 text-[10px] text-ed-text-muted">{yLabel} por {xLabel.toLowerCase()}</p>
    </div>
  );
}
